<template>
  <div class="price-filter" @click.stop>
    <div class="price-range-wrapper">
      <div class="price-range-slider">
        <div class="range-track"></div>
        <div
          class="range-progress"
          :style="{
            left: priceRangePosition.min + '%',
            width: priceRangePosition.max - priceRangePosition.min + '%',
          }"
        ></div>
        <div
          class="range-handle min"
          :style="{ left: priceRangePosition.min + '%' }"
          @mousedown="startDrag('min', $event)"
          @touchstart="startDrag('min', $event)"
        ></div>
        <div
          class="range-handle max"
          :style="{ left: priceRangePosition.max + '%' }"
          @mousedown="startDrag('max', $event)"
          @touchstart="startDrag('max', $event)"
        ></div>
      </div>
    </div>
    <div class="price-inputs">
      <div class="price-input-group">
        <label>{{ minLabel }}</label>
        <input
          type="number"
          :value="activePrice.min"
          @input="onMinPriceInput"
          @keydown="onPriceKeydown"
          min="0"
        />
      </div>
      <div class="price-input-group">
        <label>{{ maxLabel }}</label>
        <input
          type="number"
          :value="activePrice.max"
          @input="onMaxPriceInput"
          @keydown="onPriceKeydown"
          min="0"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";

export default {
  name: "PriceRangeFilter",
  props: {
    // Valeurs initiales du filtre de prix
    initialValue: {
      type: Object,
      default: () => ({
        min: undefined,
        max: undefined,
      }),
    },
    // Plage de prix disponible (min et max possibles)
    range: {
      type: Object,
      default: () => ({
        min: 0,
        max: 5000,
      }),
    },
    // Labels personnalisés
    minLabel: {
      type: String,
      default: "Min",
    },
    maxLabel: {
      type: String,
      default: "Max",
    },
    // Devise pour le formatage
    currency: {
      type: String,
      default: "EUR",
    },
    // Locale pour le formatage
    locale: {
      type: String,
      default: "fr-FR",
    },
    // Facteur de conversion (par ex. 100 pour convertir euros en centimes)
    conversionFactor: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      // Plage de prix actuelle
      priceRange: { ...this.range },
      // Prix actifs (sélectionnés par l'utilisateur)
      activePrice: {
        min:
          this.initialValue.min !== undefined
            ? Math.round(this.initialValue.min / this.conversionFactor)
            : this.range.min,
        max:
          this.initialValue.max !== undefined
            ? Math.round(this.initialValue.max / this.conversionFactor)
            : this.range.max,
      },
      // Positions du slider (en pourcentage)
      priceRangePosition: {
        min: 0,
        max: 100,
      },
      // État du drag and drop
      dragging: null,
      dragStartX: 0,
      dragStartPosition: 0,
    };
  },
  created() {
    // Initialiser les positions du slider
    this.updatePricePositions();

    // Initialiser le debounce pour le filtre de prix
    this.debouncedUpdatePriceFilter = debounce(this.applyPriceFilter, 300);

    // Ajouter les écouteurs d'événements pour le drag des sliders
    window.addEventListener("mousemove", this.handleDrag);
    window.addEventListener("mouseup", this.stopDrag);
    window.addEventListener("touchmove", this.handleDrag);
    window.addEventListener("touchend", this.stopDrag);
  },
  beforeDestroy() {
    // Nettoyer les écouteurs d'événements
    window.removeEventListener("mousemove", this.handleDrag);
    window.removeEventListener("mouseup", this.stopDrag);
    window.removeEventListener("touchmove", this.handleDrag);
    window.removeEventListener("touchend", this.stopDrag);
  },
  watch: {
    // Observer les changements dans la plage
    range: {
      handler(newRange) {
        this.priceRange = { ...newRange };
        this.updatePricePositions();
      },
      deep: true,
    },
    // Observer les changements dans la valeur initiale
    initialValue: {
      handler(newValue) {
        if (newValue.min !== undefined || newValue.max !== undefined) {
          const newActivePrice = {
            min:
              newValue.min !== undefined
                ? Math.round(newValue.min / this.conversionFactor)
                : this.priceRange.min,
            max:
              newValue.max !== undefined
                ? Math.round(newValue.max / this.conversionFactor)
                : this.priceRange.max,
          };
          this.activePrice = newActivePrice;
          this.updatePricePositions();
        }
      },
      deep: true,
    },
  },
  methods: {
    // Formater un prix
    formatPrice(price) {
      return new Intl.NumberFormat(this.locale, {
        style: "currency",
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);
    },

    // Mettre à jour les positions du slider de prix
    updatePricePositions() {
      const range = this.priceRange.max - this.priceRange.min;
      if (range === 0) return; // Éviter la division par zéro

      const minPos =
        ((this.activePrice.min - this.priceRange.min) / range) * 100;
      const maxPos =
        ((this.activePrice.max - this.priceRange.min) / range) * 100;

      this.priceRangePosition = {
        min: Math.max(0, Math.min(100, minPos)),
        max: Math.max(0, Math.min(100, maxPos)),
      };
    },

    // Gérer le début du drag pour le slider de prix
    startDrag(handle, event) {
      // Utiliser le bon événement selon le type
      const clientX = event.type.startsWith("touch")
        ? event.touches[0].clientX
        : event.clientX;

      this.dragging = handle;
      this.dragStartX = clientX;
      this.dragStartPosition = this.priceRangePosition[handle];

      // Empêcher les événements par défaut
      event.preventDefault();
      event.stopPropagation();
    },
    // Gérer le drag du slider de prix
    handleDrag(event) {
      if (!this.dragging) return;

      // Utiliser le bon événement selon le type
      const clientX = event.type.startsWith("touch")
        ? event.touches[0].clientX
        : event.clientX;

      const sliderEl = this.$el.querySelector(".price-range-slider");
      if (!sliderEl) return;

      const rect = sliderEl.getBoundingClientRect();
      const width = rect.width;

      // Calculer le déplacement relatif en pourcentage
      const deltaX = clientX - this.dragStartX;
      const deltaPercent = (deltaX / width) * 100;

      // Nouvelle position
      let newPosition = this.dragStartPosition + deltaPercent;

      // Limiter la position entre 0 et 100
      newPosition = Math.max(0, Math.min(100, newPosition));

      // Mettre à jour la position selon la poignée en cours de drag
      if (this.dragging === "min") {
        // Empêcher min de dépasser max
        newPosition = Math.min(newPosition, this.priceRangePosition.max - 5);
        this.$set(this.priceRangePosition, "min", newPosition);
      } else if (this.dragging === "max") {
        // Empêcher max de passer sous min
        newPosition = Math.max(newPosition, this.priceRangePosition.min + 5);
        this.$set(this.priceRangePosition, "max", newPosition);
      }

      // Mettre à jour les valeurs de prix actives
      this.updateActivePriceFromPositions();
    },
    // Arrêter le drag
    stopDrag() {
      if (this.dragging) {
        this.dragging = null;
        this.applyPriceFilter();
      }
    },
    // Mettre à jour les prix actifs depuis les positions
    updateActivePriceFromPositions() {
      const range = this.priceRange.max - this.priceRange.min;

      this.activePrice = {
        min: Math.round(
          this.priceRange.min + (this.priceRangePosition.min * range) / 100
        ),
        max: Math.round(
          this.priceRange.min + (this.priceRangePosition.max * range) / 100
        ),
      };
    },

    // Gérer la saisie dans le champ prix minimum
    onMinPriceInput(event) {
      const value = event.target.value;
      // Permettre la saisie vide temporairement
      if (value === '') {
        this.activePrice.min = '';
        return;
      }
      
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0) {
        this.activePrice.min = numValue;
        this.updatePriceFilter();
      }
    },

    // Gérer la saisie dans le champ prix maximum
    onMaxPriceInput(event) {
      const value = event.target.value;
      // Permettre la saisie vide temporairement
      if (value === '') {
        this.activePrice.max = '';
        return;
      }
      
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0) {
        this.activePrice.max = numValue;
        this.updatePriceFilter();
      }
    },

    // Gérer les touches spéciales
    onPriceKeydown(event) {
      // Permettre: Backspace, Delete, Tab, Escape, Enter, Home, End, flèches
      const allowedKeys = [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40];
      
      // Permettre Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (event.ctrlKey && [65, 67, 86, 88].includes(event.keyCode)) {
        return;
      }
      
      // Si c'est une touche autorisée, laisser passer
      if (allowedKeys.includes(event.keyCode)) {
        return;
      }
      
      // Permettre les chiffres (0-9) et le point décimal
      if ((event.keyCode >= 48 && event.keyCode <= 57) || 
          (event.keyCode >= 96 && event.keyCode <= 105) || 
          event.keyCode === 190 || event.keyCode === 110) {
        return;
      }
      
      // Bloquer tout le reste
      event.preventDefault();
    },

    // Mettre à jour le filtre de prix suite à une modification de l'input
    updatePriceFilter() {
      // Gérer les valeurs vides
      if (this.activePrice.min === '' || this.activePrice.min === null || this.activePrice.min === undefined) {
        this.activePrice.min = this.priceRange.min;
      }
      if (this.activePrice.max === '' || this.activePrice.max === null || this.activePrice.max === undefined) {
        this.activePrice.max = this.priceRange.max;
      }

      // Convertir en nombres
      this.activePrice.min = parseFloat(this.activePrice.min) || this.priceRange.min;
      this.activePrice.max = parseFloat(this.activePrice.max) || this.priceRange.max;

      // Corriger les valeurs si nécessaire
      if (this.activePrice.min >= this.activePrice.max) {
        this.activePrice.min = Math.max(this.priceRange.min, this.activePrice.max - 1);
      }
      
      // S'assurer que les valeurs sont dans la plage autorisée
      this.activePrice.min = Math.max(this.priceRange.min, Math.min(this.activePrice.min, this.priceRange.max));
      this.activePrice.max = Math.max(this.priceRange.min, Math.min(this.activePrice.max, this.priceRange.max));

      // Mettre à jour les positions du slider
      this.updatePricePositions();

      // Appliquer les changements avec debounce
      this.debouncedUpdatePriceFilter();
    },
    // Appliquer le filtre de prix
    applyPriceFilter() {
      // Convertir les valeurs selon le facteur de conversion
      const outputValue = {
        min: Math.round(this.activePrice.min * this.conversionFactor),
        max: Math.round(this.activePrice.max * this.conversionFactor),
      };

      // Déterminer si le prix a été modifié par rapport aux valeurs par défaut
      const isPriceChanged =
        this.activePrice.min > this.priceRange.min ||
        this.activePrice.max < this.priceRange.max;

      // Émettre les valeurs si elles ont changé
      this.$emit("change", {
        value: outputValue,
        isChanged: isPriceChanged,
      });
    },
  },
};
</script>

<style scoped>
.price-filter {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.price-range-wrapper {
  width: 100%;
  padding: 0 8px;
}

.price-range-indicators {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
  color: #666;
}

.price-range-slider {
  position: relative;
  height: 24px;
  margin-bottom: 8px;
}

.range-track {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: 4px;
  background-color: #e0e0e0;
  border-radius: 2px;
}

.range-progress {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 4px;
  background-color: #000;
  border-radius: 2px;
}

.range-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background-color: white;
  border: 2px solid #000;
  border-radius: 50%;
  cursor: pointer;
  z-index: 1;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.range-handle:hover {
  transform: translate(-50%, -50%) scale(1.1);
}

.price-inputs {
  display: flex;
  gap: 10px;
}

.price-input-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.price-input-group label {
  font-size: 12px;
  color: #666;
}

.price-input-group input {
  width: 100%;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 13px;
}

/* Cache les flèches sur les inputs numériques */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

/* Media queries pour la responsive */
@media (max-width: 768px) {
  .price-inputs {
    flex-direction: row;
  }
}
</style>
